/**
 * Coppelia theme interactions.
 * Everything here is progressive enhancement: the page works without it.
 * Each initialiser is idempotent so it can re-run when the theme editor
 * reloads a section.
 */
(function () {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* ---------- Scroll reveal ---------- */
  let revealObserver;
  function initReveal(root) {
    const items = root.querySelectorAll('[data-reveal]:not(.is-in)');
    if (reducedMotion || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-in'));
      return;
    }
    revealObserver =
      revealObserver ||
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-in');
              revealObserver.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
      );
    items.forEach((el) => revealObserver.observe(el));
  }

  /* ---------- Hero stage-light spotlight ---------- */
  function initSpotlight(root) {
    if (!finePointer || reducedMotion) return;
    root.querySelectorAll('[data-spotlight]:not([data-bound])').forEach((hero) => {
      hero.dataset.bound = '';
      let frame;
      hero.addEventListener('pointermove', (event) => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const rect = hero.getBoundingClientRect();
          hero.style.setProperty('--spot-x', `${event.clientX - rect.left}px`);
          hero.style.setProperty('--spot-y', `${event.clientY - rect.top}px`);
          hero.classList.add('is-lit');
        });
      });
      hero.addEventListener('pointerleave', () => hero.classList.remove('is-lit'));
    });
  }

  /* ---------- 3D tilt cards ---------- */
  function initTilt(root) {
    if (!finePointer || reducedMotion) return;
    root.querySelectorAll('[data-tilt]:not([data-bound])').forEach((card) => {
      card.dataset.bound = '';
      const max = parseFloat(card.dataset.tilt) || 8;
      card.addEventListener('pointermove', (event) => {
        const rect = card.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - 0.5;
        const y = (event.clientY - rect.top) / rect.height - 0.5;
        card.style.setProperty('--tilt-x', `${(-y * max).toFixed(2)}deg`);
        card.style.setProperty('--tilt-y', `${(x * max).toFixed(2)}deg`);
        card.style.setProperty('--glare-x', `${(x + 0.5) * 100}%`);
        card.style.setProperty('--glare-y', `${(y + 0.5) * 100}%`);
      });
      card.addEventListener('pointerleave', () => {
        card.style.setProperty('--tilt-x', '0deg');
        card.style.setProperty('--tilt-y', '0deg');
      });
    });
  }

  /* ---------- Magnetic buttons ---------- */
  function initMagnetic(root) {
    if (!finePointer || reducedMotion) return;
    root.querySelectorAll('.btn:not([data-bound])').forEach((btn) => {
      btn.dataset.bound = '';
      btn.addEventListener('pointermove', (event) => {
        const rect = btn.getBoundingClientRect();
        const x = event.clientX - rect.left - rect.width / 2;
        const y = event.clientY - rect.top - rect.height / 2;
        btn.style.transform = `translate(${x * 0.18}px, ${y * 0.28}px)`;
      });
      btn.addEventListener('pointerleave', () => {
        btn.style.transform = '';
      });
    });
  }

  /* ---------- Film strip: arrows + drag to scroll ---------- */
  function initFilmstrip(root) {
    root.querySelectorAll('[data-filmstrip]:not([data-bound])').forEach((wrap) => {
      wrap.dataset.bound = '';
      const track = wrap.querySelector('[data-filmstrip-track]');
      if (!track) return;
      const step = () => Math.max(track.clientWidth * 0.8, 280);

      wrap.querySelectorAll('[data-filmstrip-prev], [data-filmstrip-next]').forEach((button) => {
        button.addEventListener('click', () => {
          const direction = button.hasAttribute('data-filmstrip-prev') ? -1 : 1;
          track.scrollBy({ left: direction * step(), behavior: reducedMotion ? 'auto' : 'smooth' });
        });
      });

      const update = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        wrap.style.setProperty('--progress', max > 0 ? (track.scrollLeft / max).toFixed(3) : 1);
        wrap.toggleAttribute('data-at-start', track.scrollLeft < 4);
        wrap.toggleAttribute('data-at-end', track.scrollLeft > max);
      };
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();

      if (!finePointer) return;
      let startX = 0;
      let startScroll = 0;
      let dragging = false;
      let moved = false;
      track.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;
        dragging = true;
        moved = false;
        startX = event.clientX;
        startScroll = track.scrollLeft;
        track.classList.add('is-grabbing');
      });
      window.addEventListener('pointermove', (event) => {
        if (!dragging) return;
        const delta = event.clientX - startX;
        if (Math.abs(delta) > 4) moved = true;
        track.scrollLeft = startScroll - delta;
      });
      window.addEventListener('pointerup', () => {
        if (!dragging) return;
        dragging = false;
        track.classList.remove('is-grabbing');
      });
      track.addEventListener(
        'click',
        (event) => {
          if (moved) {
            event.preventDefault();
            event.stopPropagation();
          }
        },
        true
      );
      track.addEventListener('dragstart', (event) => event.preventDefault());
    });
  }

  /* ---------- Release countdown ---------- */
  function initCountdown(root) {
    root.querySelectorAll('[data-countdown]:not([data-bound])').forEach((el) => {
      el.dataset.bound = '';
      const target = new Date(el.dataset.countdown);
      if (isNaN(target)) return;
      const parts = {
        days: el.querySelector('[data-unit="days"]'),
        hours: el.querySelector('[data-unit="hours"]'),
        minutes: el.querySelector('[data-unit="minutes"]'),
        seconds: el.querySelector('[data-unit="seconds"]'),
      };
      const pad = (n) => String(n).padStart(2, '0');
      const tick = () => {
        const diff = target - Date.now();
        if (diff <= 0) {
          el.classList.add('is-done');
          clearInterval(timer);
          return;
        }
        parts.days.textContent = pad(Math.floor(diff / 86400000));
        parts.hours.textContent = pad(Math.floor(diff / 3600000) % 24);
        parts.minutes.textContent = pad(Math.floor(diff / 60000) % 60);
        parts.seconds.textContent = pad(Math.floor(diff / 1000) % 60);
      };
      const timer = setInterval(tick, 1000);
      tick();
    });
  }

  /* ---------- Release-day switch ----------
     Liquid renders the right state when the page is built; this keeps it
     correct for cached pages and flips it live at midnight. */
  function initRelease(root) {
    root.querySelectorAll('[data-release]:not([data-release-bound])').forEach((scope) => {
      scope.setAttribute('data-release-bound', '');
      const at = new Date(scope.dataset.release).getTime();
      if (isNaN(at)) return;
      const flip = () => scope.classList.add('is-released');
      const wait = at - Date.now();
      if (wait <= 0) {
        flip();
      } else if (wait < 2147483647) {
        setTimeout(flip, wait + 500);
      }
    });

    // Multi-stage cards: data-release-marks='{"class-name": "ISO date", ...}'
    root.querySelectorAll('[data-release-marks]:not([data-release-bound])').forEach((el) => {
      el.setAttribute('data-release-bound', '');
      let marks;
      try {
        marks = JSON.parse(el.dataset.releaseMarks);
      } catch (e) {
        return;
      }
      Object.entries(marks).forEach(([name, iso]) => {
        const at = new Date(iso).getTime();
        if (!iso || isNaN(at)) return;
        const wait = at - Date.now();
        if (wait <= 0) {
          el.classList.add(name);
        } else if (wait < 2147483647) {
          setTimeout(() => el.classList.add(name), wait + 500);
        }
      });
    });
  }

  /* ---------- Merch drop: colour, side and variant picking ---------- */
  function initMerch(root) {
    root.querySelectorAll('[data-merch]:not([data-bound])').forEach((merch) => {
      merch.dataset.bound = '';
      const stage = merch.querySelector('[data-merch-stage]');

      merch.querySelectorAll('[data-merch-colour]').forEach((swatch) => {
        swatch.addEventListener('click', () => {
          merch.querySelectorAll('[data-merch-colour]').forEach((s) => s.setAttribute('aria-pressed', 'false'));
          swatch.setAttribute('aria-pressed', 'true');
          stage.style.setProperty('--tee', swatch.dataset.colour);
          stage.style.setProperty('--ink', swatch.dataset.ink);
          stage.dataset.logo = swatch.dataset.logo;
          const label = merch.querySelector('[data-merch-colour-label]');
          if (label) label.textContent = swatch.dataset.name;
        });
      });

      merch.querySelectorAll('[data-merch-flip]').forEach((button) => {
        button.addEventListener('click', () => {
          const back = button.dataset.merchFlip === 'back';
          stage.classList.toggle('is-back', back);
          merch.querySelectorAll('[data-merch-flip]').forEach((b) =>
            b.setAttribute('aria-pressed', String(b === button))
          );
        });
      });

      merch.querySelectorAll('[data-merch-size]').forEach((pill) => {
        pill.addEventListener('click', () => {
          merch.querySelectorAll('[data-merch-size]').forEach((p) => p.setAttribute('aria-pressed', 'false'));
          pill.setAttribute('aria-pressed', 'true');
        });
      });

      // Real product: match the selected option values to a variant.
      const json = merch.querySelector('[data-merch-variants]');
      const form = merch.querySelector('[data-merch-form]');
      if (!json || !form) return;
      const variants = JSON.parse(json.textContent);
      const idInput = form.querySelector('input[name="id"]');
      const submit = form.querySelector('[type="submit"]');
      const price = merch.querySelector('[data-merch-price]');
      const stock = merch.querySelector('[data-merch-stock]');
      const low = stock ? parseInt(stock.dataset.low, 10) || 2 : 2;

      // If the size checked by default has sold out, start on the first one in stock.
      const checked = form.querySelector('[data-option]:checked');
      if (!checked || checked.disabled) {
        const firstOpen = form.querySelector('[data-option]:not(:disabled)');
        if (firstOpen) firstOpen.checked = true;
      }

      const sync = () => {
        const selected = Array.from(form.querySelectorAll('[data-option]:checked')).map((input) => input.value);
        const variant = variants.find((v) => v.options.every((value, i) => value === selected[i]));
        if (!variant) {
          submit.disabled = true;
          submit.textContent = 'Unavailable';
          if (stock) stock.textContent = '';
          return;
        }
        idInput.value = variant.id;
        submit.disabled = !variant.available;
        submit.textContent = variant.available ? 'Add to cart' : 'Sold out';
        if (price) price.textContent = variant.price;
        if (stock) {
          const size = variant.options.join(' / ');
          stock.textContent =
            variant.available && variant.left !== null && variant.left <= low
              ? `Only ${variant.left} left in ${size}`
              : '';
        }
      };
      form.addEventListener('change', sync);
      sync();
    });
  }

  /* ---------- Gallery lightbox ---------- */
  function initGallery(root) {
    root.querySelectorAll('[data-gallery]:not([data-bound])').forEach((gallery) => {
      const dialog = gallery.querySelector('[data-gallery-dialog]');
      if (!dialog || typeof dialog.showModal !== 'function') return;
      gallery.dataset.bound = '';

      const items = Array.from(gallery.querySelectorAll('[data-gallery-item]'));
      const img = dialog.querySelector('[data-gallery-img]');
      const caption = dialog.querySelector('[data-gallery-caption]');
      const count = dialog.querySelector('[data-gallery-count]');
      const total = String(items.length).padStart(2, '0');
      let index = 0;
      let opener = null;

      const show = (i) => {
        index = (i + items.length) % items.length;
        const item = items[index];
        img.classList.add('is-loading');
        img.onload = () => img.classList.remove('is-loading');
        img.src = item.dataset.full;
        img.alt = item.dataset.alt || '';
        caption.textContent = item.dataset.caption || '';
        count.textContent = item.dataset.frame ? `Frame ${item.dataset.frame} / ${total}` : '';
        // Warm the neighbours so paging feels instant.
        [index - 1, index + 1].forEach((n) => {
          new Image().src = items[(n + items.length) % items.length].dataset.full;
        });
      };

      items.forEach((item, i) => {
        item.addEventListener('click', (event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
          event.preventDefault();
          opener = item;
          show(i);
          dialog.showModal();
        });
      });

      if (items.length < 2) {
        dialog.querySelectorAll('[data-gallery-prev], [data-gallery-next]').forEach((b) => (b.hidden = true));
      }
      dialog.querySelector('[data-gallery-prev]').addEventListener('click', () => show(index - 1));
      dialog.querySelector('[data-gallery-next]').addEventListener('click', () => show(index + 1));
      dialog.querySelector('[data-gallery-close]').addEventListener('click', () => dialog.close());

      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') show(index - 1);
        if (event.key === 'ArrowRight') show(index + 1);
      });

      // Click on the dark surround (not the photo or buttons) closes.
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog || event.target.classList.contains('gallery-lightbox__figure')) {
          dialog.close();
        }
      });

      dialog.addEventListener('close', () => {
        if (opener) opener.focus({ preventScroll: true });
      });

      // Swipe to page on touch screens.
      let touchX = null;
      dialog.addEventListener('touchstart', (event) => {
        touchX = event.touches.length === 1 ? event.touches[0].clientX : null;
      }, { passive: true });
      dialog.addEventListener('touchend', (event) => {
        if (touchX === null) return;
        const delta = event.changedTouches[0].clientX - touchX;
        touchX = null;
        if (Math.abs(delta) > 50) show(index + (delta < 0 ? 1 : -1));
      });
    });
  }

  /* ---------- Ambient background videos ----------
     Muted loops play only while on screen. Reduced-motion visitors keep the
     poster frame. */
  let videoObserver;
  function initAmbientVideo(root) {
    const videos = root.querySelectorAll('video[data-ambient]:not([data-bound])');
    if (!videos.length || reducedMotion) return;
    videoObserver =
      videoObserver ||
      ('IntersectionObserver' in window
        ? new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              const video = entry.target;
              if (entry.isIntersecting) {
                const attempt = video.play();
                if (attempt && attempt.catch) attempt.catch(() => {});
              } else {
                video.pause();
              }
            });
          })
        : null);
    videos.forEach((video) => {
      video.dataset.bound = '';
      video.muted = true;
      if (videoObserver) {
        videoObserver.observe(video);
      } else {
        video.play().catch(() => {});
      }
    });
  }

  /* ---------- Spam-safe email links ----------
     The address is in the page reversed; put it back together for people. */
  function initEmails(root) {
    root.querySelectorAll('a[data-email]:not([data-bound])').forEach((link) => {
      link.dataset.bound = '';
      const address = link.dataset.email.split('').reverse().join('');
      if (!/^[^@\s]+@[^@\s]+$/.test(address)) return;
      link.href = 'mailto:' + address;
      link.textContent = address;
    });
  }

  /* ---------- Boot ---------- */
  function init(root) {
    initReveal(root);
    initSpotlight(root);
    initTilt(root);
    initMagnetic(root);
    initFilmstrip(root);
    initCountdown(root);
    initRelease(root);
    initMerch(root);
    initGallery(root);
    initAmbientVideo(root);
    initEmails(root);
  }

  init(document);

  // Theme editor support
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-in'));
    init(event.target);
  });
})();
